import { CartItem, CartItemType, StandardProducts, transformProductToCartItem } from "../contexts/heyFlowLead";
import { ENERGIE_ZUSCHLAG_EUR } from "../data/abholung";

const PREDEFINED_PRODUCT_IDS = {
  energy: 74585,       // Energiezuschlag Pferd (SKU 13119)
  pawPrint: 74586,     // Hufabdruck (SKU 13120)
  transport: 74566,    // Abholung Pferd (SKU 92000) — dyn. Preis aus PLZ
  certificate: 40874,  // Einäscherungszertifikat (shared mit Kleintier)
  diamondConsultation: 40875, // Diamantberatung (shared mit Kleintier)
};

// Leinensäckchen bleibt als Fallback-ID für "Keine Urne ausgewählt"
export const LEINEN_SAECKCHEN_PRODUCT_ID = 75101;

// Fallback wenn ein WooCommerce-Produkt fehlt (z.B. Cache nicht aktuell oder
// Produkt ausverkauft). Verhindert dass die komplette OfferDetails-Section
// crasht wenn nur ein einzelnes Produkt fehlt.
//
// fallback.image überschreibt das WooCommerce-Bild auch wenn das Produkt existiert
// — sinnvoll wenn das WC-Bild nicht passt (z.B. bei Service-Artikeln wie
// Energiezuschlag, die kein eigenes Bild haben und dann das falsche erben).
function safeCartItem(
  standardProducts: StandardProducts,
  productId: number,
  type: CartItemType,
  fallback: Partial<CartItem> & { name: string; description: string }
): CartItem {
  const entry = standardProducts[String(productId)];
  if (entry?.product) {
    const item = transformProductToCartItem(entry.product, type);
    if (fallback.image) {
      item.image = fallback.image;
    }
    // fallback.price ÜBERSCHREIBT auch wenn das WC-Produkt einen Preis hat
    // — sinnvoll für Horsia-Spezialfälle (z.B. Zertifikat kostenlos für Pferd
    // obwohl WC-Basispreis 15€ ist; das Produkt ist mit Kleintier geteilt).
    if (fallback.price !== undefined) {
      item.price = fallback.price;
    }
    return item;
  }
  return {
    name: fallback.name,
    price: fallback.price ?? 0,
    productId,
    description: fallback.description,
    image:
      fallback.image ??
      "https://animaltree.de/wp-content/uploads/2022/01/Blaetter-Logo_high_res.png",
    type,
    inscriptionIncluded: false,
  };
}

const LEAF_LOGO = "https://animaltree.de/wp-content/uploads/2022/01/Blaetter-Logo_high_res.png";

export function createStandardProductDefinitions(standardProducts: StandardProducts) {
  const pawPrint = safeCartItem(standardProducts, PREDEFINED_PRODUCT_IDS.pawPrint, "pawPrint", {
    name: "Hufabdruck",
    description: "Möchtest du, dass wir vor der Einäscherung einen Hufabdruck anfertigen?",
  });

  const certificate = safeCartItem(standardProducts, PREDEFINED_PRODUCT_IDS.certificate, "certificate", {
    name: "Einäscherungszertifikat",
    description: "Offizielles Zertifikat über die Einäscherung deines Pferdes.",
    image: "https://animaltree.de/wp-content/uploads/2026/04/Design-ohne-Titel.png",
    // Horsia: Zertifikat ist im Pferd-Sortiment kostenlos (anders als bei Kleintier).
    // Override hier, weil WC-Produkt 40874 mit Kleintier geteilt wird.
    price: 0,
  });

  const placeholderProducts: Record<CartItemType, CartItem> = {
    cremation: {
      name: "Einzelkremierung Pferd",
      price: 100,
      productId: 74568,
      description:
        "Der Preis der Einzelkremierung richtet sich nach der Widerristhöhe deines Pferdes.",
      image:
        standardProducts["74568"]?.product?.images?.[0]?.src ?? LEAF_LOGO,
      type: "cremation",
      inscriptionIncluded: false,
    },
    energy: safeCartItem(standardProducts, PREDEFINED_PRODUCT_IDS.energy, "energy", {
      name: "Energiezuschlag",
      // Zentral aus data/plz.json, damit der Platzhalter nicht vom KVA-Preis abweichen kann.
      price: ENERGIE_ZUSCHLAG_EUR,
      description: "Pauschaler Zuschlag für Energiekosten.",
      image: "https://animaltree.de/wp-content/uploads/2026/04/Design-ohne-Titel.png",
    }),
    pawPrint,
    transport: {
      name: "Abholung",
      price: -1,
      productId: 74566,
      description: "Überführung deines Pferdes zum Krematorium (Preis abhängig von der PLZ).",
      image: "https://animaltree.de/wp-content/uploads/2023/10/partnerkrematorien-animal-tree_website-2.jpg",
      type: "transport",
      inscriptionIncluded: false,
    },
    urn: {
      name: "Keine Urne ausgewählt",
      price: -1,
      productId: LEINEN_SAECKCHEN_PRODUCT_ID,
      description: "",
      image: "https://animaltree.de/wp-content/uploads/2023/10/JU4A1516-jpg.jpg",
      type: "urn",
      inscriptionIncluded: false,
    },
    inscription: {
      name: "Beschriftungen",
      price: -1,
      productId: 36098,
      description: "",
      image: "https://animaltree.de/wp-content/uploads/2023/03/Beschriftung-mit-Pusteblume.jpg",
      type: "inscription",
      inscriptionIncluded: false,
    },
    // nur als Platzhalter für den Typ; die echte Position legt das Vorsorge-Portal an
    vorsorgeCredit: {
      name: "Vorsorge-Guthaben",
      price: 0,
      productId: 0,
      description: "",
      image: LEAF_LOGO,
      type: "vorsorgeCredit",
      inscriptionIncluded: false,
      locked: true,
    },
    jewelry: {
      name: "Ascheschmuck und Andenken",
      price: -1,
      productId: 35721,
      description: "",
      image: "https://animaltree.de/wp-content/uploads/2023/03/Gedenkstein-Herz-mit-Rose.jpg",
      type: "jewelry",
      inscriptionIncluded: false,
    },
    certificate,
    diamondConsultation: safeCartItem(
      standardProducts,
      PREDEFINED_PRODUCT_IDS.diamondConsultation,
      "diamondConsultation",
      {
        name: "Diamantberatung",
        description: "Unverbindliche Beratung zur Erinnerungs-Diamanten-Herstellung.",
        image: "https://animaltree.de/wp-content/uploads/2023/10/diamantberatung-jpg-400x400.jpg",
      }
    ),
  };

  // Horsia: nur Abholung (keine Abgabe durch Halter möglich), ohne Varianten.
  // Preis wird dynamisch aus PLZ berechnet (siehe data/abholung.ts).
  // pickUp/dropOff bleibt als Struktur erhalten für Komponenten-Kompatibilität.
  const transportBasePrice = Number(standardProducts["74566"]?.product?.price ?? 0);
  const transportProducts: Record<"pickUp" | "dropOff", CartItem> = {
    pickUp: {
      name: "Abholung bei dir vor Ort",
      price: transportBasePrice,
      productId: 74566,
      description: "Überführung deines Pferdes direkt bei dir vor Ort.",
      image: "https://animaltree.de/wp-content/uploads/2023/10/partnerkrematorien-animal-tree_website-2.jpg",
      type: "transport",
      inscriptionIncluded: false,
    },
    dropOff: {
      name: "Abholung bei dir vor Ort",
      price: transportBasePrice,
      productId: 74566,
      description: "Überführung deines Pferdes direkt bei dir vor Ort.",
      image: "https://animaltree.de/wp-content/uploads/2023/10/partnerkrematorien-animal-tree_website-2.jpg",
      type: "transport",
      inscriptionIncluded: false,
    },
  };

  return { placeholderProducts, transportProducts };
}

export const productShops: Record<CartItemType, undefined | ((heyflowId: string) => string)> = {
  cremation: undefined,
  energy: function (heyflowId: string): string {
    return `/${heyflowId}/product/${PREDEFINED_PRODUCT_IDS.energy}`;
  },
  pawPrint: function (heyflowId: string): string {
    return `/${heyflowId}/product/${PREDEFINED_PRODUCT_IDS.pawPrint}`;
  },
  transport: function (heyflowId: string): string {
    return `/${heyflowId}/product/${PREDEFINED_PRODUCT_IDS.transport}`;
  },
  urn: function (heyflowId: string): string {
    return `/${heyflowId}/products/urnen`;
  },
  inscription: function (heyflowId: string): string {
    return `/${heyflowId}/products/beschriftung`;
  },
  jewelry: function (heyflowId: string): string {
    return `/${heyflowId}/products/schmuck`;
  },
  certificate: function (heyflowId: string): string {
    return `/${heyflowId}/product/${PREDEFINED_PRODUCT_IDS.certificate}`;
  },
  diamondConsultation: function (heyflowId: string): string {
    return `/${heyflowId}/product/${PREDEFINED_PRODUCT_IDS.diamondConsultation}`;
  },
  vorsorgeCredit: undefined,
};

export const anotherProduct: Record<CartItemType, string> = {
  cremation: "",
  energy: "",
  pawPrint: "Einen weiteren Hufabdruck hinzufügen",
  transport: "",
  urn: "",
  inscription: "Eine weitere Beschriftung hinzufügen",
  jewelry: "Einen weiteren Gedenkartikel hinzufügen",
  certificate: "",
  diamondConsultation: "",
  vorsorgeCredit: "",
};

export const removableProduct: Record<CartItemType, boolean> = {
  cremation: false,
  energy: true,
  pawPrint: true,
  transport: false,
  urn: false,
  inscription: true,
  jewelry: true,
  certificate: true,
  diamondConsultation: true,
  vorsorgeCredit: false,
};

export const simpleProducts: CartItemType[] = ["pawPrint", "diamondConsultation", "certificate"];
